/**
 * Shared progress / status stepper for tool pages.
 *
 * Shows a step-by-step indicator so the user always knows what's
 * happening during file reads, WASM init, processing, and output.
 * Each step transitions through: pending → active → done/error.
 *
 * Usage:
 *   const p = createProgress('#result', ['Reading file', 'Processing', 'Done']);
 *   p.activate(0);          // "Reading file" becomes active
 *   p.complete(0);          // "Reading file" becomes done
 *   p.activate(1);          // "Processing" becomes active
 *   p.complete(1);          // etc.
 *   p.finish('Converted 1.2 MB → 400 KB');   // replaces stepper with result
 *   p.error('Something broke');               // shows error state
 */

export interface Progress {
  activate(step: number): void;
  complete(step: number): void;
  finish(html: string): void;
  error(message: string): void;
}

export function createProgress(
  selector: string,
  steps: string[],
): Progress {
  const container = document.querySelector<HTMLElement>(selector);
  if (!container) throw new Error(`createProgress: "${selector}" not found`);

  container.hidden = false;
  container.classList.remove('is-error');
  container.innerHTML = '';

  const stepper = document.createElement('div');
  stepper.className = 'bytario-progress';

  const stepEls: HTMLElement[] = steps.map((label) => {
    const el = document.createElement('div');
    el.className = 'bytario-progress__step';
    el.innerHTML =
      '<span class="bytario-progress__dot"></span>' +
      '<span class="bytario-progress__label">' + escHtml(label) + '</span>';
    stepper.appendChild(el);
    return el;
  });

  container.appendChild(stepper);

  return {
    activate(i: number) {
      stepEls[i]?.classList.add('is-active');
      stepEls[i]?.classList.remove('is-done');
    },
    complete(i: number) {
      stepEls[i]?.classList.remove('is-active');
      stepEls[i]?.classList.add('is-done');
    },
    finish(html: string) {
      container.classList.remove('is-error');
      container.innerHTML = html;
    },
    error(message: string) {
      container.classList.add('is-error');
      container.innerHTML = escHtml(message);
    },
  };
}

/**
 * Yield to the browser event loop so a pending DOM paint can flush.
 * Solves the "[Violation] setTimeout handler took Xms" by letting
 * the stepper render before kicking off heavy WASM work.
 */
export function yieldToPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });
}

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c]!);
}
